import {
  FUELS,
  GENERATOR_SELL_MULTIPLIER,
  HOURS_PER_YEAR_REAL,
} from "../Constants";
import {
  DateType,
  FacilityOperatingType,
  GeneratorShoppingType,
} from "../Types";
import { getFuelPricesPerMBTU } from "../data/FuelPrices";

// Get the monthly payment amount for a new loan
// https://codepen.io/joeymack47/pen/fHwvd?editors=1010
export function getMonthlyPayment(
  principal: number,
  interestRate: number,
  months: number,
): number {
  const monthlyRate = interestRate / 12;
  return principal * (monthlyRate / (1 - Math.pow(1 + monthlyRate, -months)));
}

// Of a month's payment on an amortizing loan, how many $'s go towards interest
// (the rest goes towards principal). Depends only on what is still owed, so the payment
// amount itself is not a parameter -- it used to be one, and was never read.
export function getPaymentInterest(
  balance: number,
  interestRate: number,
): number {
  const monthlyRate = interestRate / 12;
  return balance * monthlyRate;
}

// TODO extrapolate future fuel prices over plant lifetime
export function LCWH(
  g: GeneratorShoppingType,
  date: DateType,
  feePerKgCO2e: number,
  seed: number,
) {
  const fuel = FUELS[g.fuel] || {};
  const fuelCostPerWh =
    ((getFuelPricesPerMBTU(date, seed)[g.fuel] || 0) * g.btuPerWh) / 1000000;
  const carbonCostPerWh = (feePerKgCO2e * fuel.kgCO2ePerBtu || 0) * g.btuPerWh;
  // Zero when the capacity factor estimate is zero -- an intermittent generator sampled across
  // a window with no sun or no wind in it. The cost per Wh of a plant expected to produce nothing
  // is genuinely unbounded, so this returns Infinity rather than inventing a number; the money
  // formatters render that as a dash.
  const totalWh =
    g.peakW * g.lifespanYears * HOURS_PER_YEAR_REAL * g.capacityFactor;
  const costPerWh =
    (g.buildCost +
      g.annualOperatingCost * g.lifespanYears +
      (fuelCostPerWh + carbonCostPerWh) * totalWh) /
    totalWh;
  return costPerWh;
}

// Returns how much cash the user recieves if they sell / cancel the facility
export function facilityCashBack(g: FacilityOperatingType): number {
  // Refund slightly more if construction isn't complete - after all, that money hasn't been spent yet
  // But lose more upfront from material purchases: https://www.wolframalpha.com/input/?i=10*x+%5E+1%2F2+from+0+to+100
  const percentBuilt = (g.yearsToBuild - g.yearsToBuildLeft) / g.yearsToBuild;
  const lostFromSelling =
    (g.buildCost - g.loanAmountLeft) *
    GENERATOR_SELL_MULTIPLIER *
    Math.min(1, Math.pow(percentBuilt * 10, 1 / 2));
  return g.buildCost - lostFromSelling - g.loanAmountLeft;
}

// CAC $100->150, increasing as you spend more - https://woodlawnassociates.com/electrical-potential-solar-and-competitive-electricity/
export function customersFromMarketingSpend(spend: number) {
  return Math.floor(spend / (100 + spend / 1000000));
}
